import type {RuleSeverity} from '@github-ui/code-scanning-shared/types/rule-severity'
import type {CommentAuthorAssociation as GraphQlCommentAuthorAssociation} from '@github-ui/commenting/IssueCommentHeader.graphql'
import type {CommentsPreference} from '@github-ui/diff-view-settings/types'
import type {DiffLineType, LineRange} from '@github-ui/diffs/types'
import type {ReactionViewerGroup} from '@github-ui/reaction-viewer/ReactionGroupsUtils'
import type {FragmentRefs} from 'relay-runtime'

// Used to enter dialog mode
export type EnterDialogModeFunction = (opts?: {shouldFocusFirstMarker?: boolean}, threadId?: string) => void

// TODO: rename this to be AnnotationLevel in a future PR to match ENUM on Rails.
// Plural name for ENUM is an odd convention.
export const DiffAnnotationLevels = {
  Failure: 'FAILURE',
  Notice: 'NOTICE',
  Warning: 'WARNING',
} as const

export type DiffAnnotationLevels = (typeof DiffAnnotationLevels)[keyof typeof DiffAnnotationLevels]

export type AnnotationCheckRun = {
  detailsUrl: string
  name: string
}

export type DiffAnnotation = {
  annotationLevel: DiffAnnotationLevels
  appAvatarUrl: string
  appAvatarAltText: string
  checkRun: AnnotationCheckRun
  checkSuiteName: string | null
  databaseId: number
  endLine: number
  id: string
  message: string
  path: string
  pathDigest: string
  startLine: number
  title: string
}

export type DiffSide = 'LEFT' | 'RIGHT'

export type SuggestedChangeLineRange = {
  endLineNumber: number
  endOrientation: DiffSide
  startLineNumber: number
  startOrientation: DiffSide
}

export type SuggestedChange = {
  authorLogin: string
  commentId: string
  path: string
  suggestion: string[]
  threadId: number | null | undefined
  lineRange: SuggestedChangeLineRange
}

export type ApplySuggestedChangesValidationData = {
  lineRange?: SuggestedChangeLineRange
}

export type Thread = {
  commentsData?: Comments
  diffSide?: DiffSide
  id: string
  isOutdated?: boolean
  isResolved?: boolean
  resolvedBy?: string
  subject?: ThreadSubject
  viewerCanReply?: boolean
  subjectType?: 'LINE' | 'FILE'
  line?: number | null | undefined
  author?: Author | null | undefined
  commentsConnectionId?: string
  commentCount?: number
  startLine?: number | null
  startDiffSide?: DiffSide | null
  // Optional because we try to construct threads from commit comments
  // and commit comments do not have this limit in place
  reviewCommentsLimit?: number
  reviewCommentsLimitExceeded?: boolean
}

export type ThreadSubject = {
  diffLines?: StaticDiffLine[]
  endLine?: number | null
  endDiffSide?: DiffSide
  originalEndLine?: number | null
  originalStartLine?: number | null
  startDiffSide?: DiffSide | null
  startLine?: number | null
}

export type StaticDiffLine = {
  __id: string
  left: number | null
  right: number | null
  type: DiffLineType
  html: string
  text: string
}

export const CommentReviewVariantType = {
  Vanilla: 'vanilla',
  CodeScanning: 'code_scanning',
  Copilot: 'copilot',
  Dependabot: 'dependabot',
  CodeQuality: 'code_quality',
  Automated: 'automated',
} as const

export type CommentReviewVariantType = (typeof CommentReviewVariantType)[keyof typeof CommentReviewVariantType]

export type Comments = {
  comments: Comment[]
  __id?: string
}

export type Comment = {
  author: Author | null | undefined
  authorAssociation: GraphQlCommentAuthorAssociation
  automatedComment?: AutomatedComment | null
  body: string
  bodyHTML: string
  bodyVersion?: string
  createdAt: string
  publishedAt: string | null | undefined
  currentDiffResourcePath?: string | null
  id: string
  databaseId: number | null | undefined
  isHidden: boolean
  lastUserContentEdit: UserContentEdit | null | undefined
  minimizedReason: string | null | undefined
  reactionGroups?: ReactionViewerGroup[]
  // reference is the associated PullRequest
  reference: {
    number: number | undefined
    // the commitOid if the comment is associated with a commit
    text?: string | null | undefined
    author:
      | {
          login: string
        }
      | null
      | undefined
  }
  repository: {
    id: string
    isPrivate: boolean
    name: string
    owner: {
      id: string
      login: string
      url: string
    }
  }
  reviewVariantType?: CommentReviewVariantType | null
  state: string
  viewerCanBlockFromOrg: boolean
  viewerCanMinimize: boolean
  viewerCanSeeMinimizeButton: boolean
  viewerCanSeeUnminimizeButton: boolean
  viewerCanReact?: boolean
  viewerCanReport: boolean
  viewerCanReportToMaintainer: boolean
  viewerCanUnblockFromOrg: boolean
  viewerDidAuthor: boolean
  viewerRelationship: string
  subjectType?: string | undefined
  stafftoolsUrl?: string | null
  url: string
  viewerCanDelete: boolean
  viewerCanUpdate: boolean
  ' $fragmentSpreads': FragmentRefs<'ReactionViewerRelayGroups'>
}

export type CommentWithoutFragment = Omit<Comment, ' $fragmentSpreads'>

export type Author = {
  avatarUrl: string
  id: string
  login: string
  url: string
  __typename?: 'Author'
}

export type DiffEntry = {
  path: string
  diffLines: StaticDiffLine[]
}

export type Suggestion = {
  description: string
  diffEntries: DiffEntry[]
}

export type SuggestionState = (typeof SuggestionStates)[keyof typeof SuggestionStates]
export const SuggestionStates = {
  NoSuggestion: 'no_suggestion',
  Pending: 'pending',
  Present: 'present',
  Error: 'error',
  Applied: 'applied',
} as const

export type AutomatedComment = {
  id: string
  isDismissed: boolean
  message: string
  severity: RuleSeverity
  source: AutomatedCommentSourceType
  suggestion?: Suggestion
  suggestionState: SuggestionState
  title: string
  viewerCanDismiss: boolean
  viewerCanReopen: boolean
  __typename?: 'AutomatedComment'
}

export const AutomatedCommentSource = {
  CodeScanning: 'code_scanning',
  CodeQuality: 'code_quality',
} as const

export type AutomatedCommentSourceType = (typeof AutomatedCommentSource)[keyof typeof AutomatedCommentSource]

export type CommentAuthor = {
  avatarUrl: string
  login: string
  url: string
}

export type UserContentEdit = {
  editor: Partial<Author> | null | undefined
  id?: string
  __typename?: 'UserContentEdit'
}

export type SuggestedChangesConfiguration = {
  showSuggestChangesButton: boolean
  isValidSuggestionRange: boolean
  sourceContentFromDiffLines: string | undefined
  onInsertSuggestedChange: () => void
  shouldInsertSuggestedChange?: boolean
}

export type ConfigureSuggestedChangesImplementation = {
  configureSuggestedChangesFromLineRange: (
    lineRange?: LineRange | undefined,
    shouldInsertSuggestedChange?: boolean,
  ) => SuggestedChangesConfiguration | undefined
  shouldStartNewConversationWithSuggestedChange: boolean | undefined
}

export interface ViewerData {
  avatarUrl: string
  diffViewPreference: string
  lineSpacingPreference: 'compact' | 'relaxed'
  commentsPreference: CommentsPreference
  login: string
  tabSizePreference: number
  viewerCanComment: boolean
  viewerCanApplySuggestion: boolean
}
